import type { SpinWheelVisualSegment } from './spinWheelVisual.types';

const TAU = Math.PI * 2;
const HALF_PI = Math.PI / 2;
const OUTER_RING_RATIO = 0.808;
const HUB_RATIO = 0.12;
const SEPARATOR_COLOR = 'rgba(242, 247, 239, 0.72)';
const LAMP_COUNT = 28;
const DEFAULT_WHEEL_ACCENT = '#d4af37';
const WHEEL_LABEL_FONT = '"Barlow Condensed", Bahnschrift, "Arial Narrow", sans-serif';

function labelColor(background: string): string {
  const hex = background.trim().match(/^#([\da-f]{6})$/iu)?.[1];
  if (!hex) return '#f7f3e8';

  const channels = [0, 2, 4].map((offset) => {
    const channel = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.32 ? '#182a24' : '#f7f3e8';
}

/** Resolve the shared accent contract for surfaces outside the canvas. */
function resolveCssVariable(
  value: string,
  host: HTMLElement,
  visited = new Set<string>()
): string {
  const variable = value.trim().match(/^var\(\s*(--[\w-]+)\s*(?:,\s*([^)]*))?\)$/u);
  if (!variable) return value.trim();

  const [, token, fallback = ''] = variable;
  if (visited.has(token)) return fallback.trim();
  visited.add(token);

  const tokenValue = window.getComputedStyle(host).getPropertyValue(token).trim();
  return resolveCssVariable(tokenValue || fallback, host, visited);
}

function normalizeCssColor(value: string): string | null {
  if (!value || typeof document === 'undefined') return null;

  // Theme hex colours are common; avoid mounting a DOM probe on every frame.
  const hex = value.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/iu)?.[1];
  if (hex) {
    const expanded = hex.length === 3 ? [...hex].map((digit) => digit + digit).join('') : hex;
    const channels = [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16));
    return `rgb(${channels.join(', ')})`;
  }

  const probe = document.createElement('span');
  probe.style.color = value;
  if (!probe.style.color) return null;

  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  document.body.append(probe);
  const resolved = window.getComputedStyle(probe).color;
  probe.remove();

  return resolved && resolved !== 'rgba(0, 0, 0, 0)' ? resolved : null;
}

export function resolveWheelAccent(host: HTMLElement | null): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return DEFAULT_WHEEL_ACCENT;
  }

  const scope = host ?? document.documentElement;
  const styles = window.getComputedStyle(scope);
  const candidates = [
    styles.getPropertyValue('--spin-wheel-accent'),
    styles.getPropertyValue('--color-primary'),
  ];

  for (const candidate of candidates) {
    const resolved = normalizeCssColor(resolveCssVariable(candidate, scope));
    if (resolved) return resolved;
  }

  return DEFAULT_WHEEL_ACCENT;
}

function drawOuterRings(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number, accent: string): void {
  ctx.save();
  // The casing and lamp sockets belong to the same rotating disk as the slices.
  const metal = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
  metal.addColorStop(0, '#f7faf4');
  metal.addColorStop(0.18, '#727f79');
  metal.addColorStop(0.27, accent);
  metal.addColorStop(0.36, '#e6eee7');
  metal.addColorStop(0.52, '#4b5752');
  metal.addColorStop(0.76, '#111b17');
  metal.addColorStop(0.91, '#a8b6ac');
  metal.addColorStop(1, '#f1f5ec');

  const lampBed = ctx.createLinearGradient(cx, cy - radius, cx, cy + radius);
  lampBed.addColorStop(0, '#4c5752');
  lampBed.addColorStop(0.3, '#1c2923');
  lampBed.addColorStop(0.65, '#101914');
  lampBed.addColorStop(1, '#303d34');
  const rings: [number, number, string | CanvasGradient][] = [
    [0.946, 0.092, metal],
    [0.888, 0.12, lampBed],
    [0.982, 0.012, 'rgba(241, 250, 234, 0.8)'],
    [0.964, 0.008, accent],
    [0.948, 0.008, 'rgba(0, 8, 4, 0.85)'],
    [0.826, 0.025, metal],
    [0.809, 0.008, '#111b15'],
  ];
  for (const [ratio, width, color] of rings) {
    ctx.beginPath();
    ctx.arc(cx, cy, radius * ratio, 0, TAU);
    ctx.lineWidth = Math.max(0.7, radius * width);
    ctx.strokeStyle = color;
    ctx.stroke();
  }

  ctx.restore();
}

function drawLamps(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number): void {
  ctx.save();
  for (let index = 0; index < LAMP_COUNT; index += 1) {
    const angle = (index / LAMP_COUNT) * TAU - HALF_PI;
    const x = cx + Math.cos(angle) * radius * 0.889;
    const y = cy + Math.sin(angle) * radius * 0.889;
    const lampRadius = radius * 0.024;

    const glow = ctx.createRadialGradient(x, y, lampRadius * 0.3, x, y, lampRadius * 3.2);
    glow.addColorStop(0, 'rgba(255, 242, 189, 0.8)');
    glow.addColorStop(0.35, 'rgba(255, 224, 143, 0.32)');
    glow.addColorStop(1, 'rgba(255, 207, 113, 0)');
    ctx.beginPath();
    ctx.arc(x, y, lampRadius * 3.2, 0, TAU);
    ctx.fillStyle = glow;
    ctx.fill();

    // A dark socket and a white-hot centre give each light a physical bulb.
    ctx.beginPath();
    ctx.arc(x, y, lampRadius * 1.45, 0, TAU);
    ctx.fillStyle = '#0d1611';
    ctx.fill();
    ctx.lineWidth = Math.max(0.6, radius * 0.003);
    ctx.strokeStyle = '#a5a98d';
    ctx.stroke();

    const bulb = ctx.createRadialGradient(
      x - lampRadius * 0.25, y - lampRadius * 0.3, 0, x, y, lampRadius
    );
    bulb.addColorStop(0, '#ffffff');
    bulb.addColorStop(0.48, '#fffdef');
    bulb.addColorStop(0.78, '#ffe6a0');
    bulb.addColorStop(1, '#d9a955');
    ctx.beginPath();
    ctx.arc(x, y, lampRadius, 0, TAU);
    ctx.fillStyle = bulb;
    ctx.fill();
  }
  ctx.restore();
}

function drawHub(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number, accent: string): void {
  const hubRadius = radius * HUB_RATIO;
  const metal = ctx.createLinearGradient(
    cx - hubRadius,
    cy - hubRadius,
    cx + hubRadius,
    cy + hubRadius
  );
  metal.addColorStop(0, '#fafff4');
  metal.addColorStop(0.28, '#bac8bc');
  metal.addColorStop(0.5, '#58655c');
  metal.addColorStop(0.7, '#19271e');
  metal.addColorStop(1, '#b5c3b2');

  ctx.save();
  ctx.shadowColor = 'rgba(0, 5, 2, 0.65)';
  ctx.shadowBlur = radius * 0.045;
  ctx.shadowOffsetY = radius * 0.02;
  ctx.beginPath();
  ctx.arc(cx, cy, hubRadius, 0, TAU);
  ctx.fillStyle = metal;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = Math.max(1, radius * 0.004);
  ctx.strokeStyle = '#dbe5d6';
  ctx.stroke();

  const face = ctx.createRadialGradient(
    cx - hubRadius * 0.32, cy - hubRadius * 0.4, 0, cx, cy, hubRadius * 0.82
  );
  face.addColorStop(0, '#56695b');
  face.addColorStop(0.55, '#1d3023');
  face.addColorStop(1, '#0d1a11');
  ctx.beginPath();
  ctx.arc(cx, cy, hubRadius * 0.78, 0, TAU);
  ctx.fillStyle = face;
  ctx.fill();
  ctx.lineWidth = Math.max(0.7, radius * 0.002);
  ctx.strokeStyle = accent;
  ctx.stroke();

  const jewel = ctx.createRadialGradient(cx - hubRadius * 0.1, cy - hubRadius * 0.13, 0, cx, cy, hubRadius * 0.34);
  jewel.addColorStop(0, '#f5ffe7');
  jewel.addColorStop(0.4, accent);
  jewel.addColorStop(1, '#14231a');
  ctx.beginPath();
  ctx.arc(cx, cy, hubRadius * 0.34, 0, TAU);
  ctx.fillStyle = jewel;
  ctx.fill();
  ctx.restore();
}

function drawGlassSheen(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number
): void {
  const innerRadius = radius * OUTER_RING_RATIO;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, innerRadius, 0, TAU);
  ctx.clip();

  const gloss = ctx.createLinearGradient(
    cx - innerRadius,
    cy - innerRadius,
    cx + innerRadius,
    cy + innerRadius
  );
  gloss.addColorStop(0, 'rgba(255, 255, 231, 0.28)');
  gloss.addColorStop(0.34, 'rgba(255, 255, 243, 0.04)');
  gloss.addColorStop(0.5, 'rgba(255, 255, 243, 0.14)');
  gloss.addColorStop(0.56, 'rgba(255, 255, 243, 0)');
  gloss.addColorStop(1, 'rgba(0, 9, 7, 0.28)');
  ctx.fillStyle = gloss;
  ctx.fillRect(cx - innerRadius, cy - innerRadius, innerRadius * 2, innerRadius * 2);

  const bevel = ctx.createRadialGradient(cx, cy, innerRadius * 0.7, cx, cy, innerRadius);
  bevel.addColorStop(0, 'rgba(0, 0, 0, 0)');
  bevel.addColorStop(0.82, 'rgba(0, 10, 3, 0.08)');
  bevel.addColorStop(1, 'rgba(0, 10, 3, 0.42)');
  ctx.fillStyle = bevel;
  ctx.fillRect(cx - innerRadius, cy - innerRadius, innerRadius * 2, innerRadius * 2);
  ctx.restore();
}

/** Equal slices keep backend order and the original -90 degree origin. */
function drawSegmentFills(
  ctx: CanvasRenderingContext2D,
  colors: readonly string[],
  cx: number,
  cy: number,
  radius: number
): void {
  const n = colors.length;
  if (n === 0) return;

  const innerRadius = radius * OUTER_RING_RATIO;
  const sliceAngle = TAU / n;
  const separatorAngle = n >= 2 ? Math.min(0.009, sliceAngle * 0.045) : 0;
  const colorAngle = sliceAngle - separatorAngle;

  for (let i = 0; i < n; i++) {
    const startAngle = i * sliceAngle - HALF_PI;
    const colorEndAngle = startAngle + colorAngle;

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, innerRadius, startAngle, colorEndAngle);
    ctx.closePath();
    ctx.fillStyle = colors[i];
    ctx.fill();

    if (n >= 2) {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, innerRadius, colorEndAngle, startAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = SEPARATOR_COLOR;
      ctx.fill();
    }
  }
}

/** Labels rotate with their own slice, with no outline or detached light effects. */
function drawSegmentLabels(
  ctx: CanvasRenderingContext2D,
  slices: readonly SpinWheelVisualSegment[],
  colors: readonly string[],
  cx: number,
  cy: number,
  radius: number
): void {
  const n = slices.length;
  if (n === 0) return;

  const innerRadius = radius * OUTER_RING_RATIO;
  const hubRadius = radius * HUB_RATIO;
  const sliceAngle = TAU / n;
  const labelInnerX = hubRadius + radius * 0.1;
  const labelOuterX = innerRadius - radius * 0.11;
  const availableLength = labelOuterX - labelInnerX;
  const labelCenterX = (labelInnerX + labelOuterX) / 2;
  const sliceHeight = n > 2 ? 2 * labelCenterX * Math.sin(sliceAngle / 2) : innerRadius;
  const baseFontSize = Math.max(8, Math.min(34, innerRadius * 0.12, sliceHeight * 0.65));
  const minFontSize = Math.min(9, baseFontSize);

  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  for (let i = 0; i < n; i++) {
    const midAngle = i * sliceAngle - HALF_PI + sliceAngle / 2;
    const label = slices[i].label || '';

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(midAngle);
    ctx.fillStyle = labelColor(colors[i]);

    let fontSize = baseFontSize;
    ctx.font = '600 ' + fontSize + 'px ' + WHEEL_LABEL_FONT;
    let width = ctx.measureText(label).width;
    while (width > availableLength && fontSize > minFontSize) {
      fontSize = Math.max(minFontSize, fontSize - 1);
      ctx.font = '600 ' + fontSize + 'px ' + WHEEL_LABEL_FONT;
      width = ctx.measureText(label).width;
    }

    // Keep the full configured reward label inside its radial bounds.
    const drawnWidth = Math.min(width, availableLength);
    const drawX = labelInnerX + (availableLength - drawnWidth) / 2;
    ctx.fillText(label, drawX, 0, availableLength);
    ctx.restore();
  }

  ctx.restore();
}

export function drawWheel(
  ctx: CanvasRenderingContext2D,
  slices: readonly SpinWheelVisualSegment[],
  size: number,
  rotationDeg: number,
  decorative = false
): void {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - size * 0.018;
  const accent = resolveWheelAccent(ctx.canvas ?? null);

  ctx.clearRect(0, 0, size, size);

  if (slices.length === 0 && !decorative) {
    ctx.beginPath();
    ctx.arc(cx, cy, radius * OUTER_RING_RATIO, 0, TAU);
    ctx.fillStyle = '#202b29';
    ctx.fill();
    drawOuterRings(ctx, cx, cy, radius, accent);
    drawLamps(ctx, cx, cy, radius);
    drawHub(ctx, cx, cy, radius, accent);
    return;
  }

  // Guest artwork is deliberately blank: no invented rewards or credit state.
  const colors = decorative
    ? Array.from({ length: 12 }, (_, index) => index % 2 === 0 ? accent : '#f3f1df')
    : slices.map(({ color }) => color);
  const rotRad = (rotationDeg * Math.PI) / 180;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotRad);
  ctx.translate(-cx, -cy);
  drawSegmentFills(ctx, colors, cx, cy, radius);
  if (!decorative) drawSegmentLabels(ctx, slices, colors, cx, cy, radius);
  drawOuterRings(ctx, cx, cy, radius, accent);
  drawLamps(ctx, cx, cy, radius);
  ctx.restore();

  // Reflections follow the room's light; the pointer remains outside the canvas.
  drawGlassSheen(ctx, cx, cy, radius);
  drawHub(ctx, cx, cy, radius, accent);
}

export function getSegmentIndexAtAngle(segmentCount: number, rotationDeg: number): number {
  if (segmentCount === 0) return -1;
  const sliceDeg = 360 / segmentCount;
  // Pointer sits at 3 o'clock (canvas angle 0°). Invert the rotation around
  // the +90° base offset used when drawing segments from -HALF_PI.
  const normalized = ((90 - (rotationDeg % 360)) + 360) % 360;
  return Math.floor(normalized / sliceDeg) % segmentCount;
}
